import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import BrianyCareLogo from "../../assets/auth/BrainyCare-Logo.jpg";
import dayjs from "dayjs";

const StrepCovidFluView = () => {
  const { filename } = useParams();
  const [examData, setExamData] = useState(null);

  const fetchData = async () => {
    try {
      const order_id = parseInt(filename, 10);
      if (isNaN(order_id)) {
        console.error("Invalid order ID");
        return;
      }

      const today = dayjs().format("YYYY-MM-DD");

      // Now, make the second POST request for order details
      const response = await axios.post(
        `/v2/ag/v1/${window.getPracticeId()}/orders/order/${order_id}/get`,
        {
          order_id: order_id,
          orderdate: today,
        },
      );

      if (response?.data?.data[0]) {
        const raw = response.data?.data[0];
        const patientid = raw.patientid;

        // Make the GET request to fetch patient data
        const res = await axios.get(
          `/v2/ag/v1/${window.getPracticeId()}/patient/${patientid}`,
          { params: { patientid: patientid } },
        );

        if (res.status === 200) {
          const patientData = res.data;
          console.log("Patient Data: ", patientData.data[0].dob);
          const patient_dob = patientData.data[0].dob
            ? {
                ...res.data[0],
                dob: dayjs(patientData.data[0].dob).format("MMM D, YYYY"),
              }
            : "";

          let source = "";
          const order_name = raw.ordername || "";

          if (order_name.toUpperCase() === "STREP TEST") {
            source = "Throat Swab";
          } else if (
            order_name.toUpperCase() === "FLU TEST" ||
            order_name.toUpperCase() === "COVID TEST"
          ) {
            source = "Swab of Internal Nose";
          }

          // Combine both the raw order data and patient data
          const updatedData = { ...raw, source, patient_dob };

          console.log("STREP-COVID-FLU: ", updatedData);
          setExamData(updatedData);
        }
      }
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    if (filename) fetchData();
  }, [filename]);

  return (
    <div
      className="p-6 bg-white min-h-screen w-full flex justify-center items-center"
      id={examData ? "report-ready" : undefined}
    >
      <div className="w-full max-w-3xl flex flex-col items-center border border-gray-300 rounded-md p-4 shadow-sm">
        {/* Header */}
        <div className="flex items-start  w-full justify-center">
          <img
            src={BrianyCareLogo}
            alt="C-HIM Logo"
            className="w-14 h-14 mr-3"
          />
          <div>
            <h1 className="text-xl font-bold text-start text-blue-600 leading-none">
              C-HIM
            </h1>
            <p className="text-sm font-semibold mt-1">
              Center for Hypertension & Internal Medicine
            </p>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-center font-bold text-base underline mb-4">
          Laboratory Report
        </h2>

        {/* Patient Info */}
        {examData ? (
          <>
            {/* Current Date */}
            <div className="flex justify-start text-sm text-left w-full mb-4">
              <div className="grid grid-cols-[auto_20px_1fr] gap-x-2 gap-y-1">
                <span>
                  <strong>Current Date (DOS)</strong>
                </span>
                <span>:</span>
                <span>{dayjs().format("MMM, DD YYYY")}</span>

                <span>
                  <strong>Name</strong>
                </span>
                <span>:</span>
                <span>{examData.patient_name}</span>

                <span>
                  <strong>Date of Birth</strong>
                </span>
                <span>:</span>
                <span>
                  {dayjs(examData.patient_dob.dob).format("MMM D, YYYY")}
                </span>
              </div>
            </div>

            <div className="mb-4 text-left w-full">
              <div>
                <label htmlFor="name" className="font-bold text-lg">
                  Ordered by:{" "}
                </label>
                <p className="text-sm text-gray-700">
                  Center for Hypertension & Internal Medicine
                  <br />
                  807 North Hancock Ave
                  <br />
                  Odessa, TX 79761
                </p>
              </div>
            </div>

            {/* Test Ordered */}
            <div className="mb-4 text-left w-full">
              <h3 className="font-bold">Test Ordered:</h3>
              <p className="text-sm">
                <strong>
                  Rapid Diagnostic Test (Nucleic Amplification Test NAAT)
                </strong>
              </p>
              <p className="text-sm">
                <strong>Collected:</strong>{" "}
                {dayjs(examData.collected).format("MM/DD/YYYY hh:mm A")}
              </p>
              <p className="text-sm">
                <strong>Reported:</strong>{" "}
                {dayjs(examData.reported).format("MM/DD/YYYY")}
              </p>
            </div>

            {/* Test Result */}
            <div className="mb-4 w-full text-left">
              <h3 className="font-bold">Test Result:</h3>
              <div className="grid gap-x-2 gap-y-1">
                <table className="table-auto w-full border-collapse border border-gray-300">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 border border-gray-300 font-semibold">
                        Lot No.
                      </th>
                      <th className="px-4 py-2 border border-gray-300 font-semibold">
                        Tested for
                      </th>
                      <th className="px-4 py-2 border border-gray-300 font-semibold">
                        Source
                      </th>
                      <th className="px-4 py-2 border border-gray-300 font-semibold">
                        Result (Can be one of the below)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 border border-gray-300 text-center">
                        1
                      </td>
                      <td className="px-4 py-2 border border-gray-300 text-center">
                        {examData.ordername}
                      </td>
                      <td className="px-4 py-2 border border-gray-300 text-center">
                        {examData.source}
                      </td>
                      <td className="px-4 py-2 border border-gray-300 text-center">
                        {examData.result}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <p className="text-center text-gray-500">Loading...</p>
        )}
      </div>
    </div>
  );
};

export default StrepCovidFluView;
